'use client';
import { useTranslation } from 'react-i18next';
import { Icon } from '@aqarmap/aqarmap-ui';
import { Swiper, SwiperSlide } from 'swiper/react';
import TopSellersCard from '~valuation/pages/ValuationResults/TopSellers/TopSellersCard';
import TopSellersLoader from '~valuation/pages/ValuationResults/TopSellers/TopSellersLoader';
import { Languages } from '~/constants/enums';
import { useGetTopSellersQuery } from '~/redux/api/appApiSlice';
import useSwiperNavigation from '~valuation/hooks/useSwiperNavigation';
import topSellersSliderBreakPoints from '~/modules/valuation/utils/topSellersSliderBreakPoints';

const TopSellers = ({
  propertyType,
  location,
  section,
}: {
  propertyType: { id: number; slug: string };
  location: { id: number; slug: string; titleShortPath?: string };
  section: { id: number; slug: string };
}) => {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const {
    handleInit,
    handleNext,
    handlePrev,
    handleSlideChange,
    isEnd,
    isStart,
    sliderRef,
  } = useSwiperNavigation();

  const scrollBtnStyles =
    'rounded-full bg-white w-[32px] h-[32px] hidden md:flex element-center';
  const isRtl = locale === Languages.ARABIC;

  const { data: topSellerData, isLoading } = useGetTopSellersQuery({
    locale,
    location: location.id,
    propertyType: propertyType.id,
    section: section.id,
  });

  if (isLoading) return <TopSellersLoader />;
  if (!topSellerData || topSellerData?.length === 0) return null;

  return (
    <div className="w-full mx-auto  bg-gray_light_1 rounded-[16px] p-2x">
      <div className="mb-x flex gap-x md:gap-1.5x flex-col">
        <div className="flex items-center justify-between">
          <p className="text-black text-title_4">{t('topSellersTitle')}</p>
          <div className="flex gap-2x">
            {!isStart && (
              <button
                onClick={handlePrev}
                className={`${scrollBtnStyles} ${isRtl ? 'right-4' : 'left-4'}`}
              >
                <Icon
                  name={isRtl ? 'chevron-right' : 'chevron-left'}
                  size="12px"
                  className="text-gray__dark_1"
                />
              </button>
            )}
            {!isEnd && (
              <button
                onClick={handleNext}
                className={`${scrollBtnStyles} ${isRtl ? 'left-4' : 'right-4'}`}
              >
                <Icon
                  name={isRtl ? 'chevron-left' : 'chevron-right'}
                  size="12px"
                  className="text-gray__dark_1"
                />
              </button>
            )}
          </div>
        </div>
        <p className="mb-4 text-gray__dark_1 text-body_2">
          {t('topSellersDescription', { location: location.titleShortPath })}
        </p>
      </div>

      <div className=" relative md:flex mb-4 md:gap-4 h-[72px] md:items-center">
        <Swiper
          ref={sliderRef}
          onInit={handleInit}
          onSlideChange={handleSlideChange}
          spaceBetween={16}
          autoHeight
          mousewheel={{ forceToAxis: true, releaseOnEdges: false }}
          touchStartPreventDefault={false}
          passiveListeners={false}
          breakpoints={topSellersSliderBreakPoints}
          className="!w-full"
        >
          {topSellerData?.map((item) => (
            <SwiperSlide key={item.id}>
              <TopSellersCard topSeller={item} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default TopSellers;
